import { type ReactNode, useState } from "react";
import { useDeleteTask } from "@api/task.api";
import type { Task } from "@common/types/task";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useOnDemandModal } from "./useOnDemandModal";
import { EditTaskTitleModal } from "./TaskView/EditTaskTitleModal";
import { useDuplicateTask } from "./useDuplicateTask";

interface TaskActionRow {
    kind: "item";
    id: string;
    label: string;
    onClick: () => void;
    destructive?: boolean;
    /** Set when the action is blocked, e.g. for View-only users. */
    disabledReason?: string;
}

export type TaskActionItem = TaskActionRow | { kind: "separator"; id: string };

type TaskActionDialog = "rename" | "delete";

interface UseTaskActionsArgs {
    task: Task;
    /** Edit details: opens the task with its Details section in the right sidebar. */
    onEditDetails: (task: Task) => void;
    /** Adds Open and Open chat first and a separator before Delete task, as board cards show them. */
    open?: {
        onOpen: (task: Task) => void;
        onOpenChat: (task: Task) => void;
    };
    /** Blocks Edit details, Duplicate task and Delete task with this reason. */
    editDisabledReason?: string;
    /**
     * Opens the title modal while set, such as from a board card's rename
     * shortcut; `onClose` runs as the modal closes.
     */
    renameRequest?: { onClose: () => void };
}

/**
 * A task's row actions, shared by List's row menu and the board card's menus:
 * Edit details, Duplicate task and Delete task, with the delete confirmation
 * and the title modal `renameRequest` opens. Render `dialogs` outside the
 * menus, since a menu unmounts its items as it closes.
 */
export const useTaskActions = ({
    task,
    onEditDetails,
    open,
    editDisabledReason,
    renameRequest,
}: UseTaskActionsArgs): { items: TaskActionItem[]; dialogs: ReactNode } => {
    const duplicateTask = useDuplicateTask();
    const deleteTask = useDeleteTask(task);
    const [dialog, setDialog] = useState<TaskActionDialog | null>(null);
    const { mountedSubject, isOpen, onExited } = useOnDemandModal(
        dialog ?? (renameRequest === undefined ? null : "rename"),
    );
    const closeDialog = () => setDialog(null);
    const editRow = (
        row: Pick<TaskActionRow, "id" | "label" | "onClick" | "destructive">,
    ): TaskActionRow => ({
        kind: "item",
        ...row,
        disabledReason: editDisabledReason,
    });

    const items: TaskActionItem[] = [];
    if (open !== undefined) {
        items.push(
            {
                kind: "item",
                id: "open",
                label: "Open",
                onClick: () => open.onOpen(task),
            },
            {
                kind: "item",
                id: "open-chat",
                label: "Open chat",
                onClick: () => open.onOpenChat(task),
            },
        );
    }
    items.push(
        editRow({
            id: "edit-details",
            label: "Edit details",
            onClick: () => onEditDetails(task),
        }),
        editRow({
            id: "duplicate",
            label: "Duplicate task",
            onClick: () => duplicateTask(task),
        }),
    );
    if (open !== undefined) {
        items.push({ kind: "separator", id: "separator" });
    }
    items.push(
        editRow({
            id: "delete",
            label: "Delete task",
            destructive: true,
            onClick: () => setDialog("delete"),
        }),
    );

    const dialogs = (
        <>
            {mountedSubject === "delete" && (
                <ConfirmationModal
                    show={isOpen}
                    onHide={closeDialog}
                    onExited={onExited}
                    closeButton
                    title="Delete task?"
                    question={`Are you sure you want to delete "${task.title}"?`}
                    onConfirm={() => {
                        deleteTask.mutate();
                        closeDialog();
                    }}
                    onReject={closeDialog}
                    yes="Delete"
                    no="Cancel"
                />
            )}
            {mountedSubject === "rename" && (
                <EditTaskTitleModal
                    show={isOpen}
                    task={task}
                    onClose={() => {
                        closeDialog();
                        renameRequest?.onClose();
                    }}
                    onExited={onExited}
                />
            )}
        </>
    );

    return { items, dialogs };
};

/** `useTaskActions`' items as rows of a `Menu.More`, `Menu.Root` or `Menu.Context`. */
export const TaskActionMenuItems = ({
    items,
}: Readonly<{ items: readonly TaskActionItem[] }>) =>
    items.map((item) =>
        item.kind === "separator" ? (
            <Menu.Separator key={item.id} />
        ) : (
            <Menu.Item
                key={item.id}
                destructive={item.destructive}
                disabled={item.disabledReason !== undefined}
                disabledReason={item.disabledReason}
                onClick={item.onClick}
            >
                {item.label}
            </Menu.Item>
        ),
    );
